import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { isDevelopment, getApiUrl } from '../utils/environment.js';
import { isAuthError, getAuthErrorMessage } from '../utils/authErrorUtils.js';
import AuthorizationService from '../services/authorizationService.js';

// Session expiration handler - uses origin to stay on same environment
const handleSessionExpired = (logout, errorMessage = null) => {
  localStorage.removeItem('intellegacy-session');
  const message = errorMessage || 'Your session has expired. Please log back in to continue.';
  alert(message);
  logout({ logoutParams: { returnTo: window.location.origin } });
};

// Immediate referral capture on page load
(() => {
  const urlParams = new URLSearchParams(window.location.search);
  const referralCode = urlParams.get('ref');
  
  if (referralCode) {
    localStorage.setItem('pending-referral', referralCode);
  }
})();

const UserContext = createContext();

export const useUserSession = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUserSession must be used within UserProvider');
  }
  return context;
};

/**
 * useUser hook - provides Auth0 user object directly
 * Used by Chat.js for WebSocket/DynamoDB integration (needs auth0Id/sub)
 * This is a backward-compatible hook that provides the raw Auth0 user
 */
export const useUser = () => {
  const { user } = useAuth0();
  return { user };
};

export const UserProvider = ({ children }) => {
  const { isAuthenticated, user, getAccessTokenSilently, isLoading, logout } = useAuth0();
  
  // Check if we're on localhost for dev mode - SECURE IMPLEMENTATION
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const allowDevBypass = isLocalhost && isDevelopment();
  // Stable auth state to prevent repeated effect triggers
  const [authChecked, setAuthChecked] = useState(false);
  const [lastAuthState, setLastAuthState] = useState({ isAuthenticated: null, hasUser: null });
  // Initialize state from localStorage if available
  const getInitialState = () => {
    const savedSession = localStorage.getItem('intellegacy-session');
    if (savedSession) {
      try {
        const parsed = JSON.parse(savedSession);
        return {
          ...parsed,
          isLoaded: false // Always start as not loaded to trigger API call
        };
      } catch (e) {
        // Failed to parse saved session
      }
    }
    
    return {
      isLoaded: false,
      hasPaid: false,
      reasonToJoin: '',
      termsAccepted: false,
      planType: 'Unassigned',
      authorizationGrade: 'Unassigned',
      uniqueId: null,
      email: null,
      name: null,
      isProfileVerified: false,
      needsProfileVerification: false,
      notificationsAllowed: false
    };
  };
  
  const [userSession, setUserSession] = useState(getInitialState());
  const [isLoadingSession, setIsLoadingSession] = useState(false); // Prevent duplicate calls
  
  // Request deduplication - store in-flight promise to coalesce duplicate requests
  const pendingRequestRef = useRef(null);
  const lastFetchTimeRef = useRef(0);
  const CACHE_DURATION = 5000; // 5 seconds - don't refetch within this window

  // Load user session data with caching and duplicate call prevention
  const loadUserSession = async (forceRefresh = false) => {
    if (!isAuthenticated || !user) {
      setUserSession(prev => ({ ...prev, isLoaded: true }));
      return;
    }
    
    const now = Date.now();
    
    // If we have a pending request, wait for it instead of making a new one
    if (pendingRequestRef.current) {
      return pendingRequestRef.current;
    }
    
    // Skip if recently fetched (unless forced)
    if (!forceRefresh && userSession.isLoaded && (now - lastFetchTimeRef.current) < CACHE_DURATION) {
      return;
    }

    // Prevent multiple simultaneous calls - remove redundant check since we handle with ref
    setIsLoadingSession(true);
    
    // Create the fetch promise and store it for coalescing
    const fetchPromise = (async () => {
      try {
        // Check for referral parameter from localStorage (captured on page load)
        let referredBy = null;
        const storedReferral = localStorage.getItem('pending-referral');
        if (storedReferral) {
          referredBy = storedReferral;
          localStorage.removeItem('pending-referral');
        }
        
        const token = await getAccessTokenSilently();
        const cleanApiUrl = getApiUrl().replace(/\/$/, '');
        
        const queryParams = referredBy ? `?ref=${encodeURIComponent(referredBy)}` : '';
        
        const response = await fetch(`${cleanApiUrl}/api/user/profile${queryParams}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const userData = await response.json();
          
          let verificationStatus = { 
            isProfileVerified: userData.user?.isProfileVerified || true, 
            needsProfileVerification: userData.user?.needsProfileVerification || false 
          };
          
          // Note: User-specific fields (email, phoneNumber, country, earlyAdopter, contributor)
          // are stored ONLY in the user object to avoid data duplication.
          // Components should access via userSession.user?.fieldName
          const newSessionData = {
            isLoaded: true,
            hasPaid: userData.hasPaid || false,
            reasonToJoin: userData.reasonToJoin || '',
            termsAccepted: userData.termsAccepted || false,
            planType: userData.planType || 'Unassigned',
            authorizationGrade: userData.user?.authorizationGrade || 'Unassigned',
            uniqueId: userData.uniqueId || userData.user?.uniqueId,
            // name is derived - keep at top level for convenience
            name: userData.name || (userData.user?.givenName ? `${userData.user.givenName} ${userData.user?.familyName || ''}`.trim() : userData.user?.email || userData.email),
            legacyCoins: userData.legacyCoins || 0,
            referralCount: userData.referralCount || 0,
            isProfileVerified: verificationStatus.isProfileVerified,
            needsProfileVerification: verificationStatus.needsProfileVerification,
            notificationsAllowed: userData.notificationsAllowed || userData.user?.notificationsAllowed || false,
            user: {
              ...userData.user,
              referralCount: userData.referralCount || 0,
              legacyCoins: userData.legacyCoins || 0,
              referralCode: userData.referralCode || null,
              givenName: userData.user?.givenName || '',
              familyName: userData.user?.familyName || '',
              picture: userData.user?.picture || null,
              email: userData.user?.email || userData.email,
              phoneNumber: userData.user?.phoneNumber || userData.phoneNumber || null,
              phone: userData.user?.phoneNumber || userData.phoneNumber || null,
              country: userData.user?.country || userData.country || null,
              changes: userData.user?.changes || [],
              earlyAdopter: userData.user?.earlyAdopter || userData.earlyAdopter || false,
              contributor: userData.user?.contributor || userData.contributor || false,
              isEmailVerified: userData.user?.isEmailVerified || false
            }
          };
          
          // Save to localStorage for persistence
          localStorage.setItem('intellegacy-session', JSON.stringify(newSessionData));
          lastFetchTimeRef.current = Date.now();
          setUserSession(newSessionData);
        } else {
          setUserSession(prev => ({ ...prev, isLoaded: true }));
        }
      } catch (error) {
        // Check if it's an auth/session error (refresh token, expired token, etc.)
        if (isAuthError(error)) {
          handleSessionExpired(logout, getAuthErrorMessage(error));
          return;
        }
        
        setUserSession(prev => ({ ...prev, isLoaded: true }));
      } finally {
        setIsLoadingSession(false);
        pendingRequestRef.current = null; // Clear the pending request
      }
    })();
    
    pendingRequestRef.current = fetchPromise;
    return fetchPromise;
  };

  useEffect(() => {
    if (isLoading) {
      return;
    }
    
    // Create stable auth state to prevent repeated calls
    const currentAuthState = {
      isAuthenticated,
      hasUser: !!user
    };
    
    // Only proceed if auth state has actually changed or we haven't checked yet
    const authStateChanged = !authChecked || 
      currentAuthState.isAuthenticated !== lastAuthState.isAuthenticated ||
      currentAuthState.hasUser !== lastAuthState.hasUser;
      
    if (!authStateChanged) {
      return;
    }
    
    // Mark as checked and update last known state
    setAuthChecked(true);
    setLastAuthState(currentAuthState);
    
    // Dev mode behavior
    if (allowDevBypass) {
      if (!userSession.isLoaded) {
        setUserSession(prev => ({ ...prev, isLoaded: true }));
      }
      return;
    }
    
    // Production behavior: Only load from API if authenticated with user and not already loaded
    if (currentAuthState.isAuthenticated && currentAuthState.hasUser && !userSession.isLoaded && !isLoadingSession) {
      loadUserSession();
    } else if (!currentAuthState.isAuthenticated && userSession.isLoaded !== true) {
      // Clear session if not authenticated (only if not already cleared)
      setUserSession(prev => ({ ...prev, isLoaded: true }));
    }
  }, [isLoading, isAuthenticated, !!user, authChecked, allowDevBypass, userSession.isLoaded]);

  // Activity-based session validity check - verify token on user activity after being idle
  // Much lighter than polling: only checks when user returns to the app
  useEffect(() => {
    if (!isAuthenticated || !user || isLoading || allowDevBypass) {
      return;
    }
    
    let lastActivityTime = Date.now();
    const IDLE_THRESHOLD = 5 * 60 * 1000; // 5 minutes - check session if idle longer than this
    
    const checkSessionIfIdle = async () => {
      const idleTime = Date.now() - lastActivityTime;
      
      // Only check if user was idle for more than threshold
      if (idleTime < IDLE_THRESHOLD) {
        return;
      }
      
      try {
        // Try to silently refresh the token - this will throw if session expired
        await getAccessTokenSilently({ cacheMode: 'off' });
        // Token refresh succeeded - session is still valid
      } catch (error) {
        // Session expired - handle gracefully
        if (isAuthError(error)) {
          handleSessionExpired(logout, getAuthErrorMessage(error));
        }
      }
    };
    
    // Track user activity
    const updateActivity = () => {
      lastActivityTime = Date.now();
    };
    
    // Check session when user returns to the app (tab focus, visibility)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkSessionIfIdle();
      }
    };
    
    const handleFocus = () => {
      checkSessionIfIdle();
    };
    
    // Listen for activity and return-to-app events
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('click', updateActivity);
    window.addEventListener('keydown', updateActivity);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('click', updateActivity);
      window.removeEventListener('keydown', updateActivity);
    };
  }, [isAuthenticated, user, isLoading, allowDevBypass, getAccessTokenSilently, logout]);

  // Determine what modal step user should see - SIMPLE LOGIC
  const getModalStep = () => {
    if (!userSession.isLoaded) return 1;
    
    // FIRST CHECK: If user has paid, ALWAYS go to final step
    if (userSession.hasPaid) {
      return 4; // Show referral page - they're done!
    }
    
    // User hasn't paid - check their progress
    // If they have a plan selected (not "Unassigned") OR they've accepted terms, go straight to payment
    if ((userSession.planType && userSession.planType !== 'Unassigned') || userSession.termsAccepted) {
      return 3; // Show payment page
    }
    
    // No plan selected - do they have a reason?
    if (userSession.reasonToJoin && userSession.reasonToJoin.trim() !== '') {
      return 2; // Show plan selection
    }
    
    // No reason provided - start from beginning
    return 1; // Show reason input
  };

  const updateUserSession = (updates) => {
    setUserSession(prev => {
      const newSession = { ...prev, ...updates };
      // Save to localStorage for persistence
      localStorage.setItem('intellegacy-session', JSON.stringify(newSession));
      return newSession;
    });
  };

  // Clear all session data - MUST be called on logout
  const clearSession = () => {
    // Clear all Intellegacy localStorage items
    localStorage.removeItem('intellegacy-session');
    localStorage.removeItem('pending-referral');
    
    // Reset state to initial values
    setUserSession({
      isLoaded: false,
      hasPaid: false,
      reasonToJoin: '',
      termsAccepted: false,
      planType: 'Unassigned',
      authorizationGrade: 'Unassigned',
      uniqueId: null,
      email: null,
      name: null,
      isProfileVerified: false,
      needsProfileVerification: false,
      notificationsAllowed: false
    });
    
    // Clear any pending request refs
    pendingRequestRef.current = null;
    lastFetchTimeRef.current = 0;
  };

  // Authorization helper functions - single source of truth for UI authorization
  const hasElevatedAccess = () => {
    const helper = AuthorizationService.uiHelper(userSession);
    return helper.hasElevatedAccess;
  };

  const isGodUser = () => {
    const helper = AuthorizationService.uiHelper(userSession);
    return helper.isGod;
  };

  const hasMinimumAuth = (requiredGrade) => {
    return AuthorizationService.hasMinimumGrade(userSession, requiredGrade);
  };

  const value = {
    userSession,
    isLoadingSession,
    updateUserSession,
    loadUserSession, // Expose this so components can manually refresh
    clearSession, // MUST be called on logout to clear all cached data
    getModalStep,
    hasElevatedAccess,
    isGodUser,
    hasMinimumAuth
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};
